import { useEffect, useState, useCallback } from "react";
import { Download, Loader2, X, Search, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money2, StatusBadge, OVERLINE, H1, btnPrimary, btnSecondary, useAdmin } from "@/components/admin/ui";

const STATUSES = ["", "paid", "pending", "failed", "refunded", "disputed"];
const KINDS = ["", "order", "subscription", "ad_purchase", "ad"];

function DetailDrawer({ id, onClose, onRefunded, canFinance }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.adminSaleDetail(id).then(setD).catch(() => setD(false)); }, [id]);
  const refund = async () => {
    if (!window.confirm("Issue a full refund for this transaction via Stripe?")) return;
    setBusy(true);
    try { await api.adminRefund(id); toast.success("Refund issued"); onRefunded(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50" data-testid="sale-drawer">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-[#F9F8F6] border-l border-[#E5E2DC] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC] bg-white">
          <h3 className="font-serif text-xl text-[#1F2A22]">Transaction</h3>
          <button onClick={onClose} aria-label="Close" data-testid="drawer-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        {d === null ? <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
          : d === false ? <div className="p-6 text-sm text-[#5C6B61]">Not found.</div> : (
          <div className="p-6 space-y-4">
            <div><span className={OVERLINE}>Order</span><p className="font-serif text-2xl text-[#1F2A22]">{d.order_id}</p></div>
            <div className="flex items-center gap-2"><StatusBadge status={d.payment_status} /><span className="text-xs text-[#5C6B61] uppercase">{d.kind}</span></div>
            <div className="border border-[#E5E2DC] bg-white divide-y divide-[#E5E2DC]">
              {[["Description", d.description], ["Customer", `${d.customer}${d.customer_email ? " · " + d.customer_email : ""}`], ["Seller", d.seller],
                ["Total", money2(d.total_cents)], canFinance && ["HomeRange fee", money2(d.homerange_fee_cents)], canFinance && ["Seller earnings", money2(d.seller_earnings_cents)],
                ["Fulfillment", d.fulfillment_status], ["Date", (d.date || "").slice(0, 19).replace("T", " ")]].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-2.5 text-sm"><span className="text-[#5C6B61]">{k}</span><span className="text-[#1F2A22] text-right">{v}</span></div>
              ))}
            </div>
            {canFinance && d.payment_status === "paid" && d.has_payment_intent && (
              <button onClick={refund} disabled={busy} className={`${btnSecondary} w-full`} data-testid="drawer-refund">
                {busy ? <Loader2 size={15} className="animate-spin" /> : "Issue full refund"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminSales() {
  const { admin } = useAdmin();
  const canFinance = admin?.permissions?.includes("finance");
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const pageSize = 50;

  const load = useCallback(() => {
    setRows(null);
    api.adminSales({ range: "all", status: status || undefined, kind: kind || undefined, q: q || undefined, page, page_size: pageSize })
      .then((d) => { setRows(d.rows); setTotal(d.total); }).catch(() => setRows([]));
  }, [status, kind, q, page]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const blob = await api.adminSalesExport({ range: "all" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "homerange_sales.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-sales">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Sales &amp; Orders</h1></div>
        <button onClick={exportCsv} className={btnPrimary} data-testid="sales-export"><Download size={15} /> Export CSV</button>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6B61]" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search…" className="border border-[#E5E2DC] bg-white pl-8 pr-3 py-2 text-sm outline-none focus:border-[#1F2A22]" data-testid="sales-search" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border border-[#E5E2DC] bg-white px-3 py-2 text-sm" data-testid="sales-filter-status">
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
        </select>
        <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} className="border border-[#E5E2DC] bg-white px-3 py-2 text-sm" data-testid="sales-filter-kind">
          {KINDS.map((k) => <option key={k} value={k}>{k ? k.replace("_", " ") : "All types"}</option>)}
        </select>
      </div>

      <div className="mt-4 bg-white border border-[#E5E2DC] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F2F0EB] text-[#5C6B61] uppercase tracking-wide text-[11px]">
            <tr>
              {["Order", "Date", "Type", "Customer", "Seller", "Total", canFinance && "HR Fee", "Payment", "Fulfillment"].filter(Boolean).map((h) => (
                <th key={h} className="text-left font-serif p-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={9} className="p-10 text-center"><Loader2 className="animate-spin text-[#1F2A22] inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-12 text-center text-[#5C6B61]"><Receipt size={28} className="mx-auto mb-2 text-[#E5E2DC]" />No transactions match your filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.session_id} onClick={() => setOpenId(r.session_id)} className="border-t border-[#E5E2DC] hover:bg-[#F9F8F6] cursor-pointer" data-testid="sales-row">
                <td className="p-3 font-mono text-xs">{r.order_id}</td>
                <td className="p-3 whitespace-nowrap text-[#5C6B61]">{(r.date || "").slice(0, 10)}</td>
                <td className="p-3 text-[#5C6B61] capitalize">{(r.kind || "").replace("_", " ")}</td>
                <td className="p-3 whitespace-nowrap">{r.customer}</td>
                <td className="p-3 whitespace-nowrap">{r.seller}</td>
                <td className="p-3 tabular-nums whitespace-nowrap">{money2(r.total_cents)}</td>
                {canFinance && <td className="p-3 tabular-nums whitespace-nowrap text-[#8A5A44]">{money2(r.homerange_fee_cents)}</td>}
                <td className="p-3"><StatusBadge status={r.payment_status} /></td>
                <td className="p-3 capitalize text-[#5C6B61]">{(r.fulfillment_status || "—").replace("_", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm text-[#5C6B61]">
        <span>{total} transactions</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className={`${btnSecondary} py-1.5 disabled:opacity-40`} data-testid="sales-prev">Prev</button>
          <span className="px-2 py-1.5">Page {page}</span>
          <button disabled={page * pageSize >= total} onClick={() => setPage(page + 1)} className={`${btnSecondary} py-1.5 disabled:opacity-40`} data-testid="sales-next">Next</button>
        </div>
      </div>

      {openId && <DetailDrawer id={openId} onClose={() => setOpenId(null)} onRefunded={load} canFinance={canFinance} />}
    </div>
  );
}
